/* eslint-disable react/prop-types */
import Button from 'UI/Button';
import { CaretRightIcon, Pane, Popover, Position, Text } from 'evergreen-ui';
import { BackgroundTaskStatus } from 'resources/background-tasks/background-task.schema';
import useGetBackgroundTasks from 'resources/background-tasks/useGetBackgroundTasks';
import * as userResource from 'resources/user/hooks';
import { NotificationType } from 'utils/constants';
import useSocketEvents from 'hooks/useSocketEvents';
import { useCallback, useEffect } from 'react';
import usePolling from 'hooks/usePolling';
import config from 'config/environment';
import styles from './styles.module.scss';
import AITaskCard from './AITaskCard';

const AiTaskReady = () => {
  const { data: user } = userResource.useCurrentUser();
  const { hasAdminPermissions } = userResource.useCurrentUserPermissions();

  const { data: backgroundTasks = [], isLoading, refetch: refetchBackgroundTasks } = useGetBackgroundTasks(
    {
      types: ['AI_PROCESS'],
      statuses: ['PENDING', 'COMPLETED'],
      userAuthId: !hasAdminPermissions ? user?.auth_id : undefined,
    },
    { enabled: !!user },
  );

  const isAnyTaskPending = backgroundTasks.some((task) => task.status === BackgroundTaskStatus.PENDING);

  const socketEvents = [
    {
      type: NotificationType.DOCUMENT_PROCESSING_COMPLETE,
      handler: () => {
        console.log('Socket.io, COMPLETE event. ');
        refetchBackgroundTasks();
      },
    },
    {
      type: NotificationType.DOCUMENT_PROCESSING_FAILED,
      handler: () => {
        console.log('Socket.io, FAILED event. ');
        refetchBackgroundTasks();
      },
    },
    {
      type: NotificationType.DOCUMENT_PROCESSING_STARTED,
      handler: () => {
        console.log('Socket.io, STARTED event. ');
        refetchBackgroundTasks();
      },
    },
  ];

  useSocketEvents(socketEvents, user?.id, 'Desktop Sidebar');

  const pollingCallback = useCallback(() => {
    if (config.env === 'development' || config.env === 'staging') {
      console.log('Polling triggered at', new Date().toISOString());
    }
    refetchBackgroundTasks();
  }, [refetchBackgroundTasks]);

  const { setIsPolling, isPolling } = usePolling(pollingCallback, 2 * 60 * 1000);

  useEffect(() => {
    if (isAnyTaskPending && !isPolling) {
      setIsPolling(true);
    } else if (!isAnyTaskPending && isPolling) {
      console.log('Polling stopped at', new Date().toISOString());
      setIsPolling(false);
    }
  }, [isAnyTaskPending, setIsPolling, isPolling]);

  if (isLoading || !backgroundTasks.length) {
    return null;
  }

  const props = isAnyTaskPending ? {
    title: 'Processing contract with ai...',
    subtitle: 'Keep your workflow moving - we\'ll notify you when it\'s ready for review!',
  } : {
    title: `Transaction${backgroundTasks.length > 1 ? 's' : ''} Ready for Review`,
    subtitle: `Click below to review details and finish setting your transaction${backgroundTasks.length > 1 ? 's' : ''}.`,
  };

  const first2 = backgroundTasks.slice(0, 2);

  const rest = backgroundTasks.slice(2);

  return (
    <div className={styles.notificationContainer}>
      <Pane display="flex" flexDirection="column" gap={12} borderRadius={8} backgroundColor="var(--white)" padding={12}>
        <Text fontSize={14} lineHeight="22px" fontWeight={700} color="var(--gray-600)" fontFamily="'Merriweather Sans', sans-serif" textTransform="uppercase">
          {props.title}
        </Text>
        <Text fontSize={11.5} lineHeight="16px" fontWeight={300} color="var(--gray-500)" fontFamily="'Merriweather Sans', sans-serif">
          {props.subtitle}
        </Text>
        <Pane width="100%" height={1} backgroundColor="var(--gray-200)" marginBottom={2} />
        <div className={styles.tasksContainer}>
          {
            first2.map((task) => (
              <AITaskCard key={task.item_id} task={task} />
            ))
          }
          {
            rest.length > 0 && (
              <Popover
                position={Position.RIGHT}
                content={(
                  <Pane
                    width={200}
                    padding={10}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexDirection="column"
                    className={styles.notificationContainer}
                  >
                    {
                      rest.map((task) => (
                        <AITaskCard key={task.item_id} task={task} />
                      ))
                    }
                  </Pane>
            )}
              >
                <Button
                  height={30}
                  iconAfter={<CaretRightIcon />}
                  appearance="minimal"
                >
                  (
                  {rest.length}
                  )
                  {' '}
                  more
                </Button>
              </Popover>
            )
          }
        </div>
      </Pane>
    </div>
  );
};

export default AiTaskReady;
